import React, { useState, useEffect, useMemo } from 'react';
import LZString from "lz-string";
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const Stats4x1 = () => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState('overview'); // 'overview', 'revenue', 'profitAfterAds', 'finalProfit'
    const [chartType, setChartType] = useState('cumulative'); // 'cumulative', 'daily'
    const [adsCosts, setAdsCosts] = useState({});
    const [productCosts, setProductCosts] = useState({});

    useEffect(() => {
        loadStoredData();
        processOrdersFromStorage();
    }, []);

    const loadStoredData = () => {
        const savedAdsCosts = sessionStorage.getItem("adsCosts");
        const savedProductCosts = sessionStorage.getItem("productCosts");
        
        setAdsCosts(savedAdsCosts ? JSON.parse(savedAdsCosts) : {});
        setProductCosts(savedProductCosts ? JSON.parse(savedProductCosts) : {});
    };

    const processOrdersFromStorage = async () => {
        try {
            const compressedData = sessionStorage.getItem("rawOrdersData");
            if (compressedData) {
                const decompressedData = JSON.parse(LZString.decompress(compressedData));
                console.log("Stats4x1 - Decompressed Orders Data:", decompressedData?.length || 0, "orders");
                setOrders(decompressedData || []);
            } else {
                console.warn("Stats4x1 - No compressed data found in sessionStorage.");
            }
        } catch (error) {
            console.error('Stats4x1 - Error loading orders from storage:', error);
        }
        setIsLoading(false);
    };

    // Calculate order refund amount (from Stats4)
    const calculateOrderRefundAmount = (order) => {
        let totalRefundAmount = 0;
        
        order.refunds?.forEach(refund => {
            refund.transactions?.forEach(transaction => {
                if (transaction.kind === "refund") {
                    totalRefundAmount += parseFloat(transaction.amount || 0);
                }
            });
        });
        
        return totalRefundAmount;
    };

    // Calculate COGS for delivered items only (from Stats4)
    const calculateDeliveredCOGS = (order) => {
        let totalCOGS = 0;
        
        if (!order.line_items || order.financial_status === "voided") {
            return 0;
        }
        
        order.line_items.forEach(item => {
            const productId = item.product_id?.toString();
            const originalQuantity = parseInt(item.quantity) || 0;
            
            // Calculate refunded quantity for this specific item
            let refundedQuantity = 0;
            order.refunds?.forEach(refund => {
                refund.refund_line_items?.forEach(refundItem => {
                    if (refundItem.line_item_id === item.id) {
                        refundedQuantity += parseInt(refundItem.quantity) || 0;
                    }
                });
            });
            
            // Calculate delivered/kept quantity (what customer actually received and kept)
            const deliveredQuantity = Math.max(0, originalQuantity - refundedQuantity);
            
            if (deliveredQuantity > 0) {
                if (productId && productCosts[productId] && productCosts[productId].cost) {
                    const itemCost = parseFloat(productCosts[productId].cost) || 0;
                    totalCOGS += itemCost * deliveredQuantity;
                } else {
                    // Fallback: estimate 34% of item price
                    const itemPrice = parseFloat(item.price) || 0;
                    totalCOGS += (itemPrice * 0.34) * deliveredQuantity;
                }
            }
        });
        
        return totalCOGS;
    };

    // Calculate order metrics (from Stats4) - only revenue and profit before ads per order
    const calculateOrderMetrics = (order) => {
        const currentTotalPrice = parseFloat(order.current_total_price || 0);
        const originalOrderValue = parseFloat(order.total_price || 0);
        const refundAmount = calculateOrderRefundAmount(order);
        const orderCOGS = calculateDeliveredCOGS(order);
        const isVoided = order.financial_status === "voided";
        const isStoreCredit = order.payment_gateway_names?.includes("shopify_store_credit");
        const isCardPayment = order.shipping_lines?.some(shipping => 
            shipping.code === "Livrare Expres si Plata CARD"
        );

        // Calculate costs and profit per Stats4 business rules
        let totalShippingCost = 0;
        let paymentFees = 0;
        
        const isFullRefund = refundAmount >= originalOrderValue && refundAmount > 0;
        
        if (isVoided) {
            totalShippingCost = 13.5;
            paymentFees = 0;
        } else {
            totalShippingCost = 13.5;
            
            if (isCardPayment) {
                paymentFees = (currentTotalPrice * 0.0017) + 1.25;
            }
            
            if (refundAmount > 0) {
                totalShippingCost = 27;
                paymentFees += 5;
            }
        }
        
        // Revenue calculation (same as Stats4)
        const orderRevenue = isVoided ? 0 : (isStoreCredit ? 0 : currentTotalPrice);
        
        // COGS calculation
        const adjustedCOGS = isFullRefund ? 0 : orderCOGS;
        
        // Tax calculation
        const taxableIncome = isVoided || isFullRefund ? 0 : (isStoreCredit ? 0 : orderRevenue);
        const taxAmount22 = taxableIncome > 0 ? (taxableIncome * 0.215 / 1.215) : 0;
        
        // Profit before ads (same as Stats4) - ads and fixed costs applied at daily level
        const profitBeforeAds = orderRevenue - adjustedCOGS - taxAmount22 - totalShippingCost - paymentFees;
        
        return {
            revenue: orderRevenue,
            profitBeforeAds
        };
    };

    // Process orders and create cumulative data by month
    const chartData = useMemo(() => {
        if (!orders.length) return { datasets: [] };

        // First, aggregate all orders by day to get daily totals
        const dailyData = {};
        
        orders.forEach(order => {
            // Convert to Romania timezone
            const createdAtUTC = new Date(order.created_at);
            const romaniaOffset = 2 * 60;
            const createdAtRomania = new Date(createdAtUTC.getTime() + romaniaOffset * 60 * 1000);
            const date = createdAtRomania.toISOString().split("T")[0];
            
            if (!dailyData[date]) {
                dailyData[date] = {
                    revenue: 0,
                    profitBeforeAds: 0
                };
            }
            
            const metrics = calculateOrderMetrics(order);
            dailyData[date].revenue += metrics.revenue;
            dailyData[date].profitBeforeAds += metrics.profitBeforeAds;
        });

        // Now calculate daily profit after ads and final profit
        Object.keys(dailyData).forEach(date => {
            const dayData = dailyData[date];
            const adsCost = adsCosts[date] || 0;
            
            dayData.profitAfterAds = dayData.profitBeforeAds - adsCost;
            const dateObj = new Date(date);
            const year = dateObj.getFullYear();
            const month = dateObj.getMonth(); // 0-indexed, 5 is June

            let fixedCost = 450;
            if (year > 2025 || (year === 2025 && month >= 6)) { // month >= 6 is July or later
                fixedCost = 280;
            }
            dayData.finalProfit = dayData.profitAfterAds - fixedCost;
        });

        // Group by month and day for chart display
        const monthlyData = {};
        
        Object.keys(dailyData).forEach(date => {
            const [year, month, day] = date.split('-').map(Number);
            const monthKey = `${year}-${String(month).padStart(2, '0')}`;
            const dayKey = parseInt(day);
            
            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {};
            }
            
            if (!monthlyData[monthKey][dayKey]) {
                monthlyData[monthKey][dayKey] = 0;
            }
            
            const valueToAdd = dailyData[date][viewMode] || 0;
            monthlyData[monthKey][dayKey] = valueToAdd;
        });

        // Create cumulative data for each month (sort chronologically, oldest first)
        const months = Object.keys(monthlyData).sort();
        
        // Define colors for each month (same color for same month across years)
        const monthColors = {
            '01': '#ffc9c9', // January - Pink
            '02': '#ff8a8a', // February - Blue
            '03': '#ff3b3b', // March - Yellow
            '04': '#9dc99b', // April - Teal
            '05': '#57a655', // May - Purple
            '06': '#1d731a', // June - Orange
            '07': '#86aabf', // July - Pink (different shade)
            '08': '#3f80a6', // August - Gray
            '09': '#094c73', // September - Teal (different shade)
            '10': '#9c7db0', // October - Dark Orange
            '11': '#793e9e', // November - Blue (different shade)
            '12': '#3d085e'  // December - Yellow (different shade)
        };

        // Get current date to determine which months to show by default
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        
        // Calculate which months should be visible (current + 6 months back)
        const visibleMonths = new Set();
        for (let i = 0; i <= 4; i++) {
            const targetDate = new Date(currentYear, currentMonth - 1 - i, 1);
            const monthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
            visibleMonths.add(monthKey);
        }

        const datasets = months.map((monthKey, index) => {
            const monthData = monthlyData[monthKey];
            const daysInMonth = Math.max(...Object.keys(monthData).map(Number));
            
            // Create cumulative data points
            const cumulativeData = [];
            let cumulative = 0;
            
            for (let day = 1; day <= daysInMonth; day++) {
                cumulative += monthData[day] || 0;
                cumulativeData.push({
                    x: day,
                    y: Math.round(cumulative)
                });
            }

            const [year, month] = monthKey.split('-');
            const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { 
                month: 'short', 
                year: '2-digit' 
            });

            // Get color based on month (same month = same color across years)
            const color = monthColors[month] || '#999999';
            
            // Determine if this month should be visible by default
            const isVisible = visibleMonths.has(monthKey);

            return {
                label: monthName,
                data: cumulativeData,
                borderColor: color,
                backgroundColor: color + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1.5,
                pointHoverRadius: 3,
                hidden: !isVisible, // Hide by default if not in visible months
            };
        });

        // Reverse the datasets so most recent months are rendered on top (higher z-index)
        return { datasets: datasets.reverse() };
    }, [orders, viewMode, adsCosts, productCosts]);

    // Create daily chart data (cumulative final profit)
    const dailyChartData = useMemo(() => {
        if (!orders.length) return { datasets: [], sortedDates: [] };
        
        console.log('Daily chart - Processing', orders.length, 'orders');

        // Aggregate all orders by day
        const dailyData = {};
        
        orders.forEach(order => {
            const createdAtUTC = new Date(order.created_at);
            const romaniaOffset = 2 * 60;
            const createdAtRomania = new Date(createdAtUTC.getTime() + romaniaOffset * 60 * 1000);
            const date = createdAtRomania.toISOString().split("T")[0];
            
            if (!dailyData[date]) {
                dailyData[date] = { profitBeforeAds: 0 };
            }
            
            const metrics = calculateOrderMetrics(order);
            dailyData[date].profitBeforeAds += metrics.profitBeforeAds;
        });

        // Calculate final profit per day
        Object.keys(dailyData).forEach(date => {
            const dayData = dailyData[date];
            const adsCost = adsCosts[date] || 0;
            const dateObj = new Date(date);
            const year = dateObj.getFullYear();
            const month = dateObj.getMonth(); // 0-indexed, 5 is June

            let fixedCost = 450;
            if (year > 2025 || (year === 2025 && month >= 6)) { // month >= 6 is July or later
                fixedCost = 280;
            }
            dayData.finalProfit = dayData.profitBeforeAds - adsCost - fixedCost;
        });

        // Sort dates and create cumulative dataset
        const sortedDates = Object.keys(dailyData).sort();
        console.log('Daily chart - sortedDates:', sortedDates.length, 'first few:', sortedDates.slice(0, 3));
        
        let cumulativeProfit = 0;
        const dataPoints = sortedDates.map((date, index) => {
            cumulativeProfit += dailyData[date].finalProfit;
            return {
                x: index + 1,
                y: Math.round(cumulativeProfit),
                date: date
            };
        });
        
        console.log('Daily chart - sample dataPoints:', dataPoints.slice(0, 3));
        console.log('Daily chart - total dataPoints:', dataPoints.length);

        return {
            datasets: [{
                label: 'Cumulative Final Profit',
                data: dataPoints,
                borderColor: '#dc3545',
                backgroundColor: '#dc354520',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 0,
                pointHoverRadius: 0,
                showLine: true,
            }],
            sortedDates
        };
    }, [orders, chartType, adsCosts, productCosts]);

    // Create overview bar chart data (daily revenues)
    const overviewChartData = useMemo(() => {
        if (!orders.length) return { datasets: [], labels: [] };

        // Aggregate all orders by day to get daily revenues
        const dailyData = {};
        
        orders.forEach(order => {
            const createdAtUTC = new Date(order.created_at);
            const romaniaOffset = 2 * 60;
            const createdAtRomania = new Date(createdAtUTC.getTime() + romaniaOffset * 60 * 1000);
            const date = createdAtRomania.toISOString().split("T")[0];
            
            if (!dailyData[date]) {
                dailyData[date] = 0;
            }
            
            const metrics = calculateOrderMetrics(order);
            dailyData[date] += metrics.revenue;
        });

        // Sort dates and create bar chart data
        const sortedDates = Object.keys(dailyData).sort();
        const revenues = sortedDates.map(date => Math.round(dailyData[date]));
        
        // Calculate 14-day moving average
        const movingAverage = [];
        for (let i = 0; i < revenues.length; i++) {
            const start = Math.max(0, i - 13); // 14 days including current day
            const slice = revenues.slice(start, i + 1);
            const average = slice.reduce((sum, val) => sum + val, 0) / slice.length;
            movingAverage.push(Math.round(average));
        }
        
        // Format labels to show only month when it changes
        const labels = sortedDates.map((date, index) => {
            const [year, month, day] = date.split('-');
            const currentMonthYear = `${year}-${month}`;
            const prevDate = index > 0 ? sortedDates[index - 1] : null;
            const prevMonthYear = prevDate ? prevDate.substring(0, 7) : null;
            
            // Show full date for first entry or when month changes, otherwise just day
            if (index === 0 || currentMonthYear !== prevMonthYear) {
                const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { 
                    month: 'short',
                    day: 'numeric'
                });
                return monthName;
            } else {
                return day;
            }
        });

        return {
            labels: labels,
            datasets: [
                {
                    type: 'bar',
                    label: 'Daily Revenue',
                    data: revenues,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                    order: 2
                },
                {
                    type: 'line',
                    label: '14-Day Moving Average',
                    data: movingAverage,
                    borderColor: '#000000',
                    backgroundColor: 'transparent',
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    fill: false,
                    tension: 0.1,
                    order: 1
                }
            ]
        };
    }, [orders, productCosts]);

    // Create month boundary lines for daily chart
    const getMonthBoundaries = (sortedDates) => {
        if (!sortedDates || sortedDates.length === 0) return [];
        
        const boundaries = [];
        let currentMonth = '';
        
        sortedDates.forEach((date, index) => {
            const monthKey = date.substring(0, 7); // YYYY-MM
            if (monthKey !== currentMonth && index > 0) {
                boundaries.push({
                    x: index + 1,
                    label: monthKey
                });
            }
            currentMonth = monthKey;
        });
        
        return boundaries;
    };

    // Get title and Y-axis label based on chart type and view mode
    const getChartLabels = () => {
        if (chartType === 'daily') {
            return {
                title: 'Cumulative Final Profit',
                yLabel: 'Cumulative Final Profit (RON)'
            };
        }

        switch(viewMode) {
            case 'overview':
                return {
                    title: 'Daily Revenue Overview',
                    yLabel: 'Daily Revenue (RON)'
                };
            case 'profitAfterAds':
                return {
                    title: 'Cumulative Daily Profit After Ads by Month',
                    yLabel: 'Cumulative Profit After Ads (RON)'
                };
            case 'finalProfit':
                return {
                    title: 'Cumulative Daily Final Profit by Month',
                    yLabel: 'Cumulative Final Profit (RON)'
                };
            default:
                return {
                    title: 'Cumulative Daily Revenue by Month',
                    yLabel: 'Cumulative Revenue (RON)'
                };
        }
    };

    const chartLabels = getChartLabels();

    // Overview bar chart options
    const overviewOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top'
            },
            title: {
                display: true,
                text: chartLabels.title,
                font: {
                    size: 16,
                    weight: 'bold'
                },
                padding: 20
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        if (context.dataset.type === 'line') {
                            return `14-Day Avg: ${context.parsed.y.toLocaleString()} RON`;
                        }
                        return `Revenue: ${context.parsed.y.toLocaleString()} RON`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Timeline',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxTicksLimit: 20,
                    autoSkip: true
                },
                grid: {
                    color: '#e0e0e0'
                }
            },
            y: {
                title: {
                    display: true,
                    text: chartLabels.yLabel,
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        return value.toLocaleString();
                    }
                },
                grid: {
                    color: '#e0e0e0'
                }
            }
        }
    };

    // Chart options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 10,
                    font: {
                        size: 11
                    },
                    boxWidth: 12,
                    boxHeight: 12
                }
            },
            title: {
                display: true,
                text: chartLabels.title,
                font: {
                    size: 16,
                    weight: 'bold'
                },
                padding: 20
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: function(context) {
                        return `Day ${context[0].label}`;
                    },
                    label: function(context) {
                        return `${context.dataset.label}: ${context.parsed.y.toLocaleString()} RON`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                title: {
                    display: true,
                    text: 'Day of Month',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                min: 1,
                max: 31,
                ticks: {
                    stepSize: 1,
                    callback: function(value) {
                        return Number.isInteger(value) ? value : '';
                    }
                },
                grid: {
                    color: '#e0e0e0'
                }
            },
            y: {
                title: {
                    display: true,
                    text: chartLabels.yLabel,
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        return value.toLocaleString();
                    }
                },
                grid: {
                    color: '#e0e0e0'
                }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        elements: {
            point: {
                hoverRadius: 4
            }
        }
    };

    // Daily chart options with vertical lines for month boundaries
    const dailyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            datalabels: {
                display: false
            },
            title: {
                display: true,
                text: chartLabels.title,
                font: {
                    size: 16,
                    weight: 'bold'
                },
                padding: 20
            },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        const dataPoint = context[0].raw;
                        return dataPoint.date;
                    },
                    label: function(context) {
                        return `Final Profit: ${context.parsed.y.toLocaleString()} RON`;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                title: {
                    display: true,
                    text: 'Timeline',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    stepSize: Math.ceil((dailyChartData.sortedDates?.length || 1) / 12),
                    callback: function(value, index, ticks) {
                        const sortedDates = dailyChartData.sortedDates || [];
                        const dateIndex = Math.round(value - 1);
                        
                        if (dateIndex >= 0 && dateIndex < sortedDates.length) {
                            const currentDate = sortedDates[dateIndex];
                            const [year, month] = currentDate.split('-');
                            const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { 
                                month: 'short', 
                                year: '2-digit' 
                            });
                            return monthName;
                        }
                        return '';
                    },
                    maxTicksLimit: 12
                },
                grid: {
                    color: '#e0e0e0'
                }
            },
            y: {
                title: {
                    display: true,
                    text: chartLabels.yLabel,
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        return value.toLocaleString();
                    }
                },
                grid: {
                    color: '#e0e0e0'
                }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        elements: {
            point: {
                hoverRadius: 4
            }
        }
    };

    return (
        <div className="stats3-container">
            <h1 className="stats3-header">Monthly Trajectory Comparison</h1>
            
            {/* View Mode Toggle */}
            <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px',
                backgroundColor: 'white',
                padding: '15px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <button
                    onClick={() => {
                        setChartType('cumulative');
                        setViewMode('overview');
                    }}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: (chartType === 'cumulative' && viewMode === 'overview') ? '#007bff' : 'white',
                        color: (chartType === 'cumulative' && viewMode === 'overview') ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: (chartType === 'cumulative' && viewMode === 'overview') ? 'bold' : 'normal'
                    }}
                >
                    Overview
                </button>
                <button
                    onClick={() => {
                        setChartType('cumulative');
                        setViewMode('revenue');
                    }}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: (chartType === 'cumulative' && viewMode === 'revenue') ? '#007bff' : 'white',
                        color: (chartType === 'cumulative' && viewMode === 'revenue') ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: (chartType === 'cumulative' && viewMode === 'revenue') ? 'bold' : 'normal'
                    }}
                >
                    Revenue
                </button>
                <button
                    onClick={() => {
                        setChartType('cumulative');
                        setViewMode('profitAfterAds');
                    }}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: (chartType === 'cumulative' && viewMode === 'profitAfterAds') ? '#007bff' : 'white',
                        color: (chartType === 'cumulative' && viewMode === 'profitAfterAds') ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: (chartType === 'cumulative' && viewMode === 'profitAfterAds') ? 'bold' : 'normal'
                    }}
                >
                    Profit After Ads
                </button>
                <button
                    onClick={() => {
                        setChartType('cumulative');
                        setViewMode('finalProfit');
                    }}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: (chartType === 'cumulative' && viewMode === 'finalProfit') ? '#007bff' : 'white',
                        color: (chartType === 'cumulative' && viewMode === 'finalProfit') ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: (chartType === 'cumulative' && viewMode === 'finalProfit') ? 'bold' : 'normal'
                    }}
                >
                    Final Profit
                </button>
                <button
                    onClick={() => setChartType('daily')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        backgroundColor: chartType === 'daily' ? '#28a745' : 'white',
                        color: chartType === 'daily' ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: chartType === 'daily' ? 'bold' : 'normal'
                    }}
                >
                    Cumulative Final Profit
                </button>
            </div>
            
            {isLoading ? (
                <div>Loading revenue trajectory data...</div>
            ) : (
                <>
                    {/* Chart Container */}
                    <div style={{ 
                        height: '800px', 
                        marginBottom: '30px',
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        {chartType === 'daily' ? (
                            <Line data={dailyChartData} options={dailyOptions} />
                        ) : viewMode === 'overview' ? (
                            <Line data={overviewChartData} options={overviewOptions} />
                        ) : (
                            <Line data={chartData} options={options} />
                        )}
                    </div>




                </>
            )}
        </div>
    );
};

export default Stats4x1; 